import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";

// Critical path - load immediately
import LandingPage from "@/pages/LandingPage";

// Lazy load all other pages for better performance
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const TunerPage = lazy(() => import("@/pages/TunerPage"));
const MetronomePage = lazy(() => import("@/pages/MetronomePage"));
const PDFLibraryPage = lazy(() => import("@/pages/PDFLibraryPage"));
const VideosPage = lazy(() => import("@/pages/VideosPage"));
const VideoPlayerPage = lazy(() => import("@/pages/VideoPlayerPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const BuyBaglamaWizard = lazy(() => import("@/pages/BuyBaglamaWizard"));
const VirtualSazPage = lazy(() => import("@/pages/VirtualSazPage"));
const DonationPage = lazy(() => import("@/pages/DonationPage"));
const DonationSuccessPage = lazy(() => import("@/pages/DonationSuccessPage"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
  </div>
);

const API = '/api';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        // Get token from localStorage for incognito/Safari fallback
        const token = localStorage.getItem('auth_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers
        });
        if (!response.ok) {
          // Clear invalid token
          localStorage.removeItem('auth_token');
          throw new Error('Not authenticated');
        }
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Protected Route - Gizli URL'e yönlendirir
const AdminProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        // Get admin token from localStorage for session persistence
        const adminToken = localStorage.getItem('adminToken');
        
        // If no token, not authenticated
        if (!adminToken) {
          setIsAuthenticated(false);
          return;
        }
        
        const response = await fetch(`${API}/admin/me`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });
        
        if (!response.ok) {
          // Clear invalid token
          localStorage.removeItem('adminToken');
          localStorage.removeItem('admin');
          throw new Error('Not authenticated');
        }
        
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAdmin();
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/yonetim-paneli-2026" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();
  
  // Handle session_id from Emergent Auth - check URL fragment synchronously
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/kayit" element={<LoginPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        
        {/* Free Tools - No login required */}
        <Route path="/baglama-akort" element={<TunerPage />} />
        <Route path="/tuner" element={<Navigate to="/baglama-akort" replace />} />
        <Route path="/akort" element={<Navigate to="/baglama-akort" replace />} />
        <Route path="/metronome" element={<MetronomePage />} />
        <Route path="/turku-notalari" element={<PDFLibraryPage />} />
        <Route path="/notalar" element={<Navigate to="/turku-notalari" replace />} />
        <Route path="/nota-arsivi" element={<Navigate to="/turku-notalari" replace />} />
        <Route path="/sanal-baglama" element={<VirtualSazPage />} />
        <Route path="/sanal-saz" element={<Navigate to="/sanal-baglama" replace />} />
        
        {/* Video Routes - SEO friendly with slug */}
        <Route path="/baglama-dersleri" element={<VideosPage />} />
        <Route path="/dersler" element={<Navigate to="/baglama-dersleri" replace />} />
        <Route path="/baglama-dersleri/:videoSlug" element={<VideoPlayerPage />} />
        <Route path="/dersler/:videoSlug" element={<Navigate to="/baglama-dersleri/:videoSlug" replace />} />
        
        {/* Pricing */}
        <Route path="/fiyatlar" element={<PricingPage />} />
        <Route path="/odeme-basarili" element={<PaymentSuccessPage />} />
        
        {/* Legal Pages */}
        <Route path="/legal/:type" element={<LegalPage />} />
        
        {/* Blog Routes */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        
        {/* Buy Baglama Wizard */}
        <Route path="/satin-al" element={<BuyBaglamaWizard />} />
        
        {/* Donation Pages */}
        <Route path="/bagis" element={<DonationPage />} />
        <Route path="/bagis-basarili" element={<DonationSuccessPage />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes - Gizli URL ile korumalı */}
        {/* Eski /admin URL'leri artık 404 döner */}
        <Route path="/admin" element={<Navigate to="/" replace />} />
        <Route path="/admin/login" element={<Navigate to="/" replace />} />
        
        {/* Gerçek admin girişi - Gizli URL */}
        <Route path="/yonetim-paneli-2026" element={<AdminLogin />} />
        <Route path="/yonetim-paneli-2026/*" element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
          <Toaster position="top-right" />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
